import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Subject } from 'rxjs';
import moment from 'moment';
import { takeUntil } from 'rxjs/operators';
import { DepartureVM } from '../../../../../models/ArrivalDepartureModel';
import { ArrivalDepartureService } from '../../../../../services/arrivaldeparture.service';
import { DatePipe } from '@angular/common';
import { ArrivalDepartureDetailsComponent } from '../arrivaldeparture-details/arrivaldeparture-details.component';
import { DepartureNewComponent } from '../departure-new/departure-new.component';
import { MyConfigOrganization } from '../../../../../models/myConfig';
import { Subscription } from 'rxjs';
import { BackOffice24StatesService } from '../../state/back-office24-states.service';
import { MyConfigService } from '../../../../../services/my-config.service';

@Component({
  selector: 'app-departure-list',
  templateUrl: './departure-list.component.html',
  styleUrls: ['./departure-list.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class DepartureListComponent implements OnInit, OnDestroy {
  dateFrom: string;
  dateTo: string;
  timeFrom: string;
  timeTo: string;
  departureForm: UntypedFormGroup;
  departures: DepartureVM[];
  dataSource: MatTableDataSource<DepartureVM> = new MatTableDataSource<DepartureVM>([]);
  @Output() incrementRequests: EventEmitter<void> = new EventEmitter<void>();
  @Output() decrementRequests: EventEmitter<void> = new EventEmitter<void>();
  @Input() isLoading: boolean = false;
  orgSubscription: Subscription = new Subscription();
  @Input() organization: MyConfigOrganization;

  filtersExpanded: boolean = true;
  availableStatuses: string[] = [
    "Departed",
    "Deleted",
    "Scheduled",
    "New"
  ];

  departuresLastUpdated: Date;

  displayedColumns: string[] = [
    'stationName',
    'terminalName',
    'departureCode',
    'operator',
    'scheduledDeparture',
    'estimatedDeparture',
    'actualDeparture',
    'flightDepartureDate',
    'status',
    'remarks',
    'arrivalStation',
    'gate',
    'checkInStatus',
    'checkInDeskFrom',
    'checkInDeskTo',
    'updated',
    'orderShuttles'
  ];

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  componentDestroyed$: Subject<boolean> = new Subject();

  constructor(
    public arrivalDepartureService: ArrivalDepartureService,
    public dialog: MatDialog,
    public router: Router,
    private fb: UntypedFormBuilder,
    public datepipe: DatePipe,
    public backOffice24StatesService: BackOffice24StatesService,
    public myConfigService: MyConfigService
  ) { }

  ngOnInit(): void {
    this.orgSubscription = this.backOffice24StatesService.currentOrganization$.subscribe(
      org => this.organization = org
    );
    this.initArrivals();
  }

  initArrivals() {
    this.dateFrom = moment().format();
    this.timeFrom = "00:00";
    this.dateTo = moment().add(2, 'd').format();
    this.timeTo = "00:00";
    this.initDepartureForm();
    this.getDepartures();
  }

  initDepartureForm() {
    this.departureForm = this.fb.group({
      dateFrom: [this.dateFrom],
      timeFrom: [this.timeFrom],
      dateTo: [this.dateTo],
      timeTo: [this.timeTo],
      statusFilter: [[]],  // Array for multiple selection
      shuttleFilter: ['withShuttles']  // Default to show all flights
    });

    // Subscribe to form changes to apply filters automatically
    this.departureForm.valueChanges.pipe(
      takeUntil(this.componentDestroyed$)
    ).subscribe(() => {
      this.applyAdvancedFilters();
    });
  }

  toggleFilters(): void {
    this.filtersExpanded = !this.filtersExpanded;
  }

  clearFilters(): void {
    this.departureForm.patchValue({
      statusFilter: [],
      shuttleFilter: 'all'
    });
    this.applyAdvancedFilters();
  }

  applyAdvancedFilters(): void {
    if (!this.departures) return;

    let filteredData = [...this.departures];
    const formValues = this.departureForm.value;

    if (formValues.statusFilter && formValues.statusFilter.length > 0) {
      filteredData = filteredData.filter(departure =>
        formValues.statusFilter.includes(departure.status)
      );
    }

    if (formValues.shuttleFilter === 'withShuttles') {
      filteredData = filteredData.filter(departure =>
        departure.orderShuttles && departure.orderShuttles > 0
      );
    } else if (formValues.shuttleFilter === 'withoutShuttles') {
      filteredData = filteredData.filter(departure =>
        !departure.orderShuttles || departure.orderShuttles === 0
      );
    }

    this.dataSource.data = filteredData;

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  flatten(obj: any) {
    return Object.keys(obj).reduce((acc, current) => {
      const _key = `${current}`;
      const currentValue = obj[current];
      if (Array.isArray(currentValue) || Object(currentValue) === currentValue) {
        Object.assign(acc, this.flatten(currentValue));
      } else {
        acc[_key] = currentValue;
      }
      return acc;
    }, {});
  }

  getDepartures() {
    this.incrementRequests.emit();
    this.setDateFormats();
    this.departures = [];
    this.arrivalDepartureService
      .getDeparturesStationBetweenDepartureDate(this.organization.config.stations[0], this.dateFrom, this.dateTo)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: (departures) => {
          this.departures = departures;
          this.departuresLastUpdated = new Date();
          this.dataSource = new MatTableDataSource<DepartureVM>(this.departures);
          this.dataSource.paginator = this.paginator;
          this.dataSource.sort = this.sort;
          this.applyAdvancedFilters();
          this.decrementRequests.emit();
        },
        error: (error) => {
          this.decrementRequests.emit();
          console.error('DepartureListComponent - Error in getDepartures', error);
        }
      });
  }

  showSelected(departure: DepartureVM): void {
    this.dialog.open(ArrivalDepartureDetailsComponent, {
      maxHeight: '95vh',
      maxWidth: '95vw',
      hasBackdrop: true,
      data: {
        ...departure,
        arrivalId: null,
        departureId: departure.departureId,
        isShuttles: departure.orderShuttles
      }
    });
  }

  createDeparture(): void {
    this.dialog.open(DepartureNewComponent, {
      maxHeight: '95vh',
      maxWidth: '95vw',
      hasBackdrop: true
    });
  }

  setDateFormats() {
    this.dateFrom = this.datepipe.transform(this.departureForm.controls['dateFrom'].value, 'yyyy-MM-dd') + ' ' + this.departureForm.controls['timeFrom'].value;
    this.dateTo = this.datepipe.transform(this.departureForm.controls['dateTo'].value, 'yyyy-MM-dd') + ' ' + this.departureForm.controls['timeTo'].value;
  }

  ngOnDestroy() {
    this.orgSubscription.unsubscribe();
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }
}
